import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Plus, Search, X, Edit2, Building2, ChevronDown, ChevronRight } from 'lucide-react'
import HelpLink from '../components/HelpLink'

const EMPTY = {
  name: '', contact_name: '', email: '', phone: '',
  hq_street: '', hq_city: '', hq_state: '', hq_zip: '', hq_country: '',
  ship_from_same_as_hq: true,
  ship_from_street: '', ship_from_city: '', ship_from_state: '', ship_from_zip: '', ship_from_country: '',
  shipping_notes: '', shipping_hours: '', shipping_appointment_required: false,
  payment_terms: '',
  lead_time_production_days: '', lead_time_air_days: '', lead_time_sea_days: '', lead_time_ground_days: '',
  default_transit_mode: 'ground', order_cycle_months: 3,
  deposit_pct: '', deposit_trigger: '', balance_trigger: '', balance_net_days: '',
  notes: ''
}

const TRANSIT_FIELD = { ground: 'lead_time_ground_days', sea: 'lead_time_sea_days', air: 'lead_time_air_days' }

// Planning lead time = production days + transit days for the supplier's default transit mode.
// Returns null if either piece is missing.
export function supplierLeadTimeDays(s) {
  if (!s) return null
  const prod = Number(s.lead_time_production_days)
  const transit = Number(s[TRANSIT_FIELD[s.default_transit_mode || 'ground']])
  if (!prod || !transit) return null
  return prod + transit
}

const numOrNull = v => (v === '' || v === null || v === undefined ? null : Number(v))
// Lead times can't be negative: blank or negative → not set
const posOrNull = v => (v === '' || v === null || v === undefined || Number(v) < 0 ? null : Number(v))

// Quick-fill presets for the structured payment terms (used by Cash Out planning).
const TERM_PRESETS = {
  'Net 30': { deposit_pct: '', deposit_trigger: '', balance_trigger: 'ship', balance_net_days: 30 },
  'Net 45': { deposit_pct: '', deposit_trigger: '', balance_trigger: 'ship', balance_net_days: 45 },
  'Net 60': { deposit_pct: '', deposit_trigger: '', balance_trigger: 'ship', balance_net_days: 60 },
  'Net 90': { deposit_pct: '', deposit_trigger: '', balance_trigger: 'ship', balance_net_days: 90 },
  '30% deposit, 70% net 30 post shipment': { deposit_pct: 30, deposit_trigger: 'po', balance_trigger: 'ship', balance_net_days: 30 },
  '30% at PO, 70% at shipment': { deposit_pct: 30, deposit_trigger: 'po', balance_trigger: 'ship', balance_net_days: 0 },
  '50% deposit, 50% on shipment': { deposit_pct: 50, deposit_trigger: 'po', balance_trigger: 'ship', balance_net_days: 0 },
  '100% upfront': { deposit_pct: 100, deposit_trigger: 'po', balance_trigger: 'po', balance_net_days: 0 },
  'COD': { deposit_pct: '', deposit_trigger: '', balance_trigger: 'receipt', balance_net_days: 0 },
}
const TRIGGER_WORDS = { po: 'at PO', ship: 'at shipment', receipt: 'on receipt' }

// Human-readable summary of the structured terms, saved to payment_terms for the supplier list.
function termsSummary(f) {
  if (!f.balance_trigger) return ''
  const dep = Number(f.deposit_pct) || 0
  const net = Number(f.balance_net_days) || 0
  const bal = `${net ? `net ${net} after ` : ''}${net ? (f.balance_trigger === 'po' ? 'PO' : f.balance_trigger === 'ship' ? 'shipment' : 'receipt') : TRIGGER_WORDS[f.balance_trigger]}`
  if (dep >= 100) return `100% ${TRIGGER_WORDS[f.deposit_trigger] || 'upfront'}`
  if (!dep) return net ? `Net ${net}${f.balance_trigger !== 'ship' ? ` after ${f.balance_trigger === 'po' ? 'PO' : 'receipt'}` : ''}` : `100% ${TRIGGER_WORDS[f.balance_trigger]}`
  return `${dep}% deposit ${TRIGGER_WORDS[f.deposit_trigger] || ''}, ${100 - dep}% ${bal}`.replace(/\s+,/, ',')
}

function Section({ title, open, onToggle, children, alwaysOpen }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
      {!alwaysOpen && (
        <button onClick={onToggle} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-3)', border: 'none', cursor: 'pointer', color: 'var(--text-2)', fontFamily: 'var(--font)', fontSize: '13px', fontWeight: 500 }}>
          {title}
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      )}
      {alwaysOpen && (
        <div style={{ padding: '10px 14px', background: 'var(--bg-3)', fontSize: '13px', fontWeight: 500, color: 'var(--text-2)' }}>{title}</div>
      )}
      {(open || alwaysOpen) && <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>{children}</div>}
    </div>
  )
}

export default function Suppliers() {
  const { activeBrand } = useAuth()
  const [suppliers, setSuppliers] = useState([])
  const [search, setSearch] = useState('')
  const [modal, setModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [shipOpen, setShipOpen] = useState(false)
  const [leadOpen, setLeadOpen] = useState(false)

  useEffect(() => { if (activeBrand) load() }, [activeBrand])

  async function load() {
    setLoading(true)
    const { data } = await supabase.from('suppliers').select('*').eq('brand_id', activeBrand.id).order('name')
    setSuppliers(data || [])
    setLoading(false)
  }

  function openNew() { setEditing(null); setForm(EMPTY); setShipOpen(false); setLeadOpen(false); setModal(true) }
  function openEdit(s) {
    setEditing(s)
    setForm({ ...EMPTY, ...s, lead_time_production_days: s.lead_time_production_days || '', lead_time_air_days: s.lead_time_air_days || '', lead_time_sea_days: s.lead_time_sea_days || '', lead_time_ground_days: s.lead_time_ground_days || '', default_transit_mode: s.default_transit_mode || 'ground', order_cycle_months: s.order_cycle_months ?? 3, deposit_pct: s.deposit_pct ?? '', deposit_trigger: s.deposit_trigger || '', balance_trigger: s.balance_trigger || '', balance_net_days: s.balance_net_days ?? '', ship_from_same_as_hq: s.ship_from_same_as_hq !== false,
      // Older suppliers only have the text terms: pre-fill the structured fields from the matching preset
      ...(!s.balance_trigger && TERM_PRESETS[s.payment_terms] ? TERM_PRESETS[s.payment_terms] : {}) })
    setShipOpen(true); setLeadOpen(true); setModal(true)
  }
  function closeModal() { setModal(false); setEditing(null) }
  const sf = k => v => setForm(f => ({ ...f, [k]: v }))

  async function save() {
    setSaving(true)
    const payload = { ...form, brand_id: activeBrand.id, lead_time_production_days: posOrNull(form.lead_time_production_days), lead_time_air_days: posOrNull(form.lead_time_air_days), lead_time_sea_days: posOrNull(form.lead_time_sea_days), lead_time_ground_days: posOrNull(form.lead_time_ground_days), default_transit_mode: form.default_transit_mode || 'ground', order_cycle_months: Math.max(1, Number(form.order_cycle_months) || 3), deposit_pct: numOrNull(form.deposit_pct), deposit_trigger: form.deposit_trigger || null, balance_trigger: form.balance_trigger || null, balance_net_days: numOrNull(form.balance_net_days), payment_terms: termsSummary(form) || form.payment_terms || null }
    if (editing) await supabase.from('suppliers').update(payload).eq('id', editing.id)
    else await supabase.from('suppliers').insert(payload)
    setSaving(false); closeModal(); load()
  }

  const filtered = suppliers.filter(s => s.name.toLowerCase().includes(search.toLowerCase()) || (s.hq_country || '').toLowerCase().includes(search.toLowerCase()))

  return (
    <div className="page">
      <div className="page-header">
        <h1>Suppliers<HelpLink section="suppliers" /></h1>
        <div className="page-header-actions">
          <div className="search-input-wrap"><Search size={14} /><input className="input" placeholder="Search suppliers..." value={search} onChange={e => setSearch(e.target.value)} /></div>
          <button className="btn btn-primary" onClick={openNew}><Plus size={14} />Add Supplier</button>
        </div>
      </div>

      <div className="card">
        {loading ? <div style={{ padding: '40px', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          : filtered.length === 0 ? <div className="empty-state"><Building2 size={40} /><p>No suppliers yet</p></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Contact</th><th>Email</th><th>Country</th><th>Payment Terms</th><th>Production Lead</th><th>Planning Lead Time</th><th></th></tr></thead>
                <tbody>
                  {filtered.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 500 }}>{s.name}</td>
                      <td>{s.contact_name || '—'}</td>
                      <td>{s.email ? <a href={`mailto:${s.email}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>{s.email}</a> : '—'}</td>
                      <td>{s.hq_country || '—'}</td>
                      <td style={{ fontSize: '12px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.payment_terms || '—'}</td>
                      <td>{s.lead_time_production_days ? `${s.lead_time_production_days}d` : '—'}</td>
                      <td>{supplierLeadTimeDays(s) ? `${supplierLeadTimeDays(s)}d (${s.default_transit_mode || 'ground'})` : <span className="badge badge-warning" style={{ fontSize: '10px' }}>Needs lead time</span>}</td>
                      <td><button className="btn btn-ghost btn-sm" onClick={() => openEdit(s)}><Edit2 size={13} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal modal-lg" style={{ maxWidth: '680px' }}>
            <div className="modal-header">
              <h2>{editing ? 'Edit Supplier' : 'Add Supplier'}</h2>
              <button className="btn btn-ghost btn-sm" onClick={closeModal}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Company Name *</label><input className="input" value={form.name} onChange={e => sf('name')(e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Contact Name</label><input className="input" value={form.contact_name} onChange={e => sf('contact_name')(e.target.value)} /></div>
              </div>
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Email</label><input className="input" type="email" value={form.email} onChange={e => sf('email')(e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Phone</label><input className="input" value={form.phone} onChange={e => sf('phone')(e.target.value)} /></div>
              </div>

              <Section title="HQ Address" alwaysOpen>
                <input className="input" placeholder="Street address" value={form.hq_street} onChange={e => sf('hq_street')(e.target.value)} />
                <div className="form-grid-3">
                  <input className="input" placeholder="City" value={form.hq_city} onChange={e => sf('hq_city')(e.target.value)} />
                  <input className="input" placeholder="State / Province" value={form.hq_state} onChange={e => sf('hq_state')(e.target.value)} />
                  <input className="input" placeholder="ZIP / Postal" value={form.hq_zip} onChange={e => sf('hq_zip')(e.target.value)} />
                </div>
                <input className="input" placeholder="Country" value={form.hq_country} onChange={e => sf('hq_country')(e.target.value)} />
              </Section>

              <Section title="Ship From Address" open={shipOpen} onToggle={() => setShipOpen(o => !o)}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.ship_from_same_as_hq} onChange={e => sf('ship_from_same_as_hq')(e.target.checked)} />
                  Same as HQ address
                </label>
                {!form.ship_from_same_as_hq && <>
                  <input className="input" placeholder="Street address" value={form.ship_from_street} onChange={e => sf('ship_from_street')(e.target.value)} />
                  <div className="form-grid-3">
                    <input className="input" placeholder="City" value={form.ship_from_city} onChange={e => sf('ship_from_city')(e.target.value)} />
                    <input className="input" placeholder="State" value={form.ship_from_state} onChange={e => sf('ship_from_state')(e.target.value)} />
                    <input className="input" placeholder="ZIP" value={form.ship_from_zip} onChange={e => sf('ship_from_zip')(e.target.value)} />
                  </div>
                  <input className="input" placeholder="Country" value={form.ship_from_country} onChange={e => sf('ship_from_country')(e.target.value)} />
                </>}
                <div className="form-group"><label className="form-label">Shipping Hours</label><input className="input" placeholder="e.g. Mon–Fri 8am–5pm" value={form.shipping_hours} onChange={e => sf('shipping_hours')(e.target.value)} /></div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.shipping_appointment_required} onChange={e => sf('shipping_appointment_required')(e.target.checked)} />
                  Appointment required for pickup
                </label>
                <div className="form-group"><label className="form-label">Shipping Notes</label><textarea className="textarea" rows={2} placeholder="Dock info, special instructions..." value={form.shipping_notes} onChange={e => sf('shipping_notes')(e.target.value)} /></div>
              </Section>

              <Section title="Lead Times" open={leadOpen} onToggle={() => setLeadOpen(o => !o)}>
                <div className="form-grid">
                  <div className="form-group"><label className="form-label">Production (days)</label><input className="input" type="number" min="0" placeholder="e.g. 45" value={form.lead_time_production_days} onChange={e => sf('lead_time_production_days')(e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Air Transit (days)</label><input className="input" type="number" min="0" placeholder="e.g. 5" value={form.lead_time_air_days} onChange={e => sf('lead_time_air_days')(e.target.value)} /></div>
                </div>
                <div className="form-grid">
                  <div className="form-group"><label className="form-label">Sea Transit (days)</label><input className="input" type="number" min="0" placeholder="e.g. 30" value={form.lead_time_sea_days} onChange={e => sf('lead_time_sea_days')(e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Ground Transit (days)</label><input className="input" type="number" min="0" placeholder="e.g. 5" value={form.lead_time_ground_days} onChange={e => sf('lead_time_ground_days')(e.target.value)} /></div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Default Transit Mode (used for planning)</label>
                    <select className="select" value={form.default_transit_mode} onChange={e => sf('default_transit_mode')(e.target.value)}>
                      <option value="ground">Ground</option>
                      <option value="sea">Sea</option>
                      <option value="air">Air</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Order Cycle (months per PO)</label>
                    <input className="input" type="number" min="1" placeholder="3" value={form.order_cycle_months} onChange={e => sf('order_cycle_months')(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Planning Lead Time</label>
                    <div style={{ padding: '8px 0', fontSize: '13px', color: 'var(--text-2)' }}>
                      {supplierLeadTimeDays(form) ? `${supplierLeadTimeDays(form)} days (production + ${form.default_transit_mode} transit)` : 'Needs production and transit days'}
                    </div>
                  </div>
                </div>
              </Section>

              <Section title="Payment Terms" alwaysOpen>
                <div className="form-group">
                  <label className="form-label">Quick fill</label>
                  <select className="select" value="" onChange={e => { const t = TERM_PRESETS[e.target.value]; if (t) setForm(f => ({ ...f, ...t })) }}>
                    <option value="">— Choose common terms to fill the fields below —</option>
                    {Object.keys(TERM_PRESETS).map(t => <option key={t}>{t}</option>)}
                  </select>
                  <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '6px' }}>{termsSummary(form) ? `Saved as: ${termsSummary(form)}` : 'Leave blank if unknown — this supplier will be left out of Cash Out until set.'}</div>
                </div>
                <div className="form-grid">
                  <div className="form-group"><label className="form-label">Deposit %</label><input className="input" type="number" min="0" max="100" placeholder="e.g. 50" value={form.deposit_pct} onChange={e => sf('deposit_pct')(e.target.value)} /></div>
                  <div className="form-group">
                    <label className="form-label">Deposit Due</label>
                    <select className="select" value={form.deposit_trigger} onChange={e => sf('deposit_trigger')(e.target.value)}>
                      <option value="">— None / unknown —</option>
                      <option value="po">When PO is written</option>
                      <option value="ship">When goods ship</option>
                    </select>
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Balance Due</label>
                    <select className="select" value={form.balance_trigger} onChange={e => sf('balance_trigger')(e.target.value)}>
                      <option value="">— Unknown —</option>
                      <option value="po">When PO is written</option>
                      <option value="ship">When goods ship</option>
                      <option value="receipt">When goods are received</option>
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Net Days After Balance Trigger</label><input className="input" type="number" min="0" placeholder="e.g. 30" value={form.balance_net_days} onChange={e => sf('balance_net_days')(e.target.value)} /></div>
                </div>
              </Section>

              <div className="form-group"><label className="form-label">Internal Notes</label><textarea className="textarea" rows={2} value={form.notes} onChange={e => sf('notes')(e.target.value)} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.name}>{saving ? 'Saving...' : editing ? 'Update Supplier' : 'Add Supplier'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
